<template>
    <div v-bind="$attrs" @click="tryOpenModal" class="cursor-pointer">
        <slot></slot>
    </div>
    <ModalCustom
        :is-open="state.isLoadModalOpen"
        @close="tryCloseLoadModal"
        title="Load Play"
    >
        <div class="w-[80vw] max-h-[50vh] overflow-y-auto">
            <loading-circle class="m-12" v-if="state.isLoading" />
            <div v-else class="mt-2 p-4 flex gap-4">
                <div
                    class="
                        grid grid-cols-3
                        gap-4
                        lg:grid-cols-4
                        flex-grow flex-shrink
                    "
                >
                    <div
                        v-if="playInActivePlaybook.length === 0"
                        class="col-span-4 self-center"
                    >
                        <div class="text-center text-gray-500">
                            No plays in this playbook
                        </div>
                    </div>
                    <div
                        v-for="play in playInActivePlaybook"
                        class="
                            p-8
                            bg-white
                            shadow-xl
                            rounded-lg
                            border border-gray-200
                            text-center
                            cursor-pointer
                            hover:border-primary
                        "
                        @click="loadPlay(play)"
                    >
                        <div class="block text-xl">
                            {{ play.play_name }}
                        </div>
                        <div class="mt-2">
                            <img
                                :src="
                                    play.thumbnail_url ??
                                    `${previewUrl}/${play.play_preview}`
                                "
                                alt="Play Thumbnail"
                                class="
                                    bg-white
                                    flex-grow flex-shrink
                                    w-full
                                    rounded-md
                                    my-4
                                    object-contain
                                "
                                :style="{
                                    backgroundImage: `linear-gradient(to bottom, #fffa, #fffa), url('${play.background}')`,
                                    backgroundSize: 'contain',
                                    backgroundRepeat: 'no-repeat',
                                    aspectRatio: 2.07339829981,
                                }"
                                onerror="this.onerror=null;this.src='/default-images/dummy-play.png';this.style.backgroundImage = 'none';"
                            />
                        </div>
                    </div>
                </div>
                <div
                    class="
                        flex-grow-0 flex-shrink-0
                        w-[25%]
                        bg-white
                        rounded-md
                        shadow-md
                        p-4
                    "
                >
                    <h4 class="text-xl font-medium mb-2">Playbooks</h4>
                    <ul>
                        <li
                            class="text-gray-500 p-2 mb-2"
                            v-if="state.playbooks.length === 0"
                        >
                            No Playbooks Found
                        </li>
                        <template v-else>
                            <li
                                class="mb-2 ml-0 p-0"
                                v-for="playbook in state.playbooks"
                            >
                                <button
                                    type="button"
                                    @click="state.activePlaybook = playbook"
                                    class="
                                        block
                                        py-2
                                        px-3
                                        bg-gray-200
                                        rounded-md
                                        hover:text-white hover:bg-primary
                                        w-full
                                    "
                                    :class="{
                                        'bg-primary text-white':
                                            state.activePlaybook
                                                ?.playbook_id ===
                                            playbook.playbook_id,
                                    }"
                                >
                                    {{ playbook.playbook_title }}
                                </button>
                            </li>
                        </template>
                    </ul>
                </div>
            </div>
        </div>
    </ModalCustom>
</template>

<script setup>
import { computed, onMounted, reactive } from "vue";
import LoadingCircle from "./LoadingCircle.vue";
import ModalCustom from "./ModalCustom.vue";
import AppButton from "./AppButton.vue";

const props = defineProps([
    "previewUrl",
    "playbookApi",
    "playApi",
    "whiteboardApi",
    "showNewPlayButton",
]);
const emit = defineEmits(["loaded", "opened"]);
const state = reactive({
    isLoadModalOpen: false,
    activePlaybook: null,
    playbooks: [],
});

const playInActivePlaybook = computed(() => {
    return state.activePlaybook?.plays ?? [];
});

const tryCloseLoadModal = () => {
    if (state.isLoading) {
        return;
    }

    state.isLoadModalOpen = false;
};

async function tryOpenModal() {
    emit("opened");
    state.isLoadModalOpen = true;
    state.isLoading = true;
    try {
        await refreshData();
    } catch {}
    state.isLoading = false;
}

const loadPlay = async (selectedPlay) => {
    if (!selectedPlay) {
        const customEvent = new CustomEvent("load-play-modal.play-loaded", {
            detail: null,
        });
        document.dispatchEvent(customEvent);
        state.isLoadModalOpen = false;
        return;
    }
    const dataPlay = await axios.get("/plays/get-play-data", {
        params: {
            play_id: selectedPlay.play_id,
        },
    });
    const playJson = JSON.parse(dataPlay.data.play_json);
    state.isLoading = true;
    try {
        const frame = document.getElementById("meeting_iframe");
        if (frame) {
            frame.contentWindow.postMessage(
                {
                    action: "play",
                    data: playJson,
                },
                props.whiteboardApi
            );
        }
    } catch {
        alert("Error loading play");
    }
    state.isLoading = false;
    state.isLoadModalOpen = false;
    const customEvent = new CustomEvent("load-play-modal.play-loaded", {
        detail: {
            play: playJson,
            background: selectedPlay.background,
        },
    });
    document.dispatchEvent(customEvent);
    emit("loaded", dataPlay.data);
};

async function refreshData() {
    const code = new URLSearchParams(window.location.search).get("code");
    state.playbooks = (
        await axios.get(props.playbookApi, {
            params: {
                code,
            },
        })
    ).data;
    state.activePlaybook = state.playbooks[0];
}

onMounted(() => {
    refreshData();
});
</script>

<style scoped></style>
